import React from "react";
import { useCountry } from "../contexts/CountryContext";
import { getHomeContent } from "../data/homeContent";

const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2,6 5,9 10,3" />
  </svg>
);

const CrossIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="3" y1="3" x2="9" y2="9" />
    <line x1="9" y1="3" x2="3" y2="9" />
  </svg>
);

const DashIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="3" y1="6" x2="9" y2="6" />
  </svg>
);

const VARIANTS = {
  yes: { bg: "#6fa8fa", iconColor: "#fff", icon: <CheckIcon /> },
  gray: { bg: "#dde3ed", iconColor: "#5a6a85", icon: <CheckIcon /> },
  no: { bg: "#edf1f9", iconColor: "#b0bccf", icon: <CrossIcon /> },
  partial: {
    bg: "#eaf2ff",
    iconColor: "#6fa8fa",
    icon: <DashIcon />,
    border: "1.5px solid #93c4fb",
  },
};

function Cell({ variant, label }) {
  const { bg, iconColor, icon, border } = VARIANTS[variant];
  return (
    <div className="comparison-cell" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div
        className={`comparison-icon comparison-icon-${variant}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: bg,
          border: border || "none",
          color: iconColor,
        }}
      >
        {icon}
      </div>
      {label && (
        <span style={{ fontSize: 11.5, fontWeight: 300, color: "#9ca3af", marginTop: 4 }}>
          {label}
        </span>
      )}
    </div>
  );
}

const LEGEND = [
  { color: "#6fa8fa", border: null, label: "OPTRA Labs supported" },
  { color: "#dde3ed", border: null, label: "Consultant supported" },
  { color: "#edf1f9", border: null, label: "Not supported" },
];

/** What each mark means, for the stacked mobile list and for screen readers. */
const VARIANT_TEXT = {
  yes: "Supported",
  gray: "Supported",
  no: "Not supported",
  partial: "Partly supported",
};

/**
 * "OPTRA Labs vs Grant Consultants". The comparison is the same in every region; the consultant
 * column title and the spelling of the rows come from the homepage content pack
 * (src/data/homeContent.js). Below the `md` breakpoint the table becomes a stacked list: one
 * block per feature with the two marks labelled, so phones see the comparison too.
 */
export default function ComparisonTable() {
  const { country } = useCountry();
  const { rows, competitorTitle, competitorSubtitle } = getHomeContent(country).comparison;
  const competitorName = `${competitorTitle} ${competitorSubtitle}`;
  return (
    <div className="comparison-table-wrap" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif", fontWeight: 300 }}>
      <ul className="comparison-stack md:hidden" aria-label="Feature comparison" style={{ listStyle: "none", margin: 0, padding: 0, border: "1.5px solid #d1ddf5", borderRadius: 8, overflow: "hidden" }}>
        {rows.map((row, i) => (
          <li
            key={i}
            className="comparison-stack-row"
            style={{ padding: "14px 16px", borderBottom: i < rows.length - 1 ? "1.5px solid #edf1f9" : "none", background: "#fff" }}
          >
            <p className="comparison-stack-feature" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "#1e293b", fontWeight: 300 }}>
              {row.feature}
            </p>
            <div className="comparison-stack-marks" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
              <div className="comparison-stack-mark comparison-optra-cell" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#eef5ff", border: "1px solid #dce8fb" }}>
                <Cell variant={row.optra} />
                <span style={{ fontSize: 12, lineHeight: 1.3, color: "#0C447C" }}>
                  <span style={{ display: "block", fontWeight: 500 }}>OPTRA Labs</span>
                  <span style={{ display: "block", color: "#5a6a85" }}>{row.optraLabel || VARIANT_TEXT[row.optra]}</span>
                </span>
              </div>
              <div className="comparison-stack-mark comparison-consultant-cell" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#f9fafb", border: "1px solid #edf1f9" }}>
                <Cell variant={row.consultant} />
                <span style={{ fontSize: 12, lineHeight: 1.3, color: "#374151" }}>
                  <span style={{ display: "block", fontWeight: 500 }}>{competitorName}</span>
                  <span style={{ display: "block", color: "#5a6a85" }}>{row.consultantLabel || VARIANT_TEXT[row.consultant]}</span>
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden md:block" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <div style={{ border: "1.5px solid #d1ddf5", borderRadius: 8, overflow: "hidden", minWidth: 640 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead>
            <tr style={{ borderBottom: "1.5px solid #d1ddf5" }}>
              <th
                style={{
                  width: "52%",
                  padding: "18px 24px",
                  textAlign: "left",
                  fontSize: 11.5,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  fontWeight: 400,
                  background: "#fff",
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 2,
                }}
              >
                Feature
              </th>
              <th style={{ width: "24%", padding: "18px 0", textAlign: "center", background: "#e8f1fe", borderLeft: "1.5px solid #d1ddf5", position: "sticky", top: 0, zIndex: 3, boxShadow: "inset 0 -1px 0 #c8dcfb" }}>
                <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#0C447C" }}>
                  OPTRA Labs
                </span>
                <span style={{ display: "block", fontSize: 11.5, fontWeight: 300, color: "#6fa8fa", marginTop: 3, letterSpacing: "0.04em" }}>
                  Platform
                </span>
              </th>
              <th style={{ width: "24%", padding: "18px 0", textAlign: "center", background: "#f5f6f8", borderLeft: "1.5px solid #d1ddf5", position: "sticky", top: 0, zIndex: 2 }}>
                <span style={{ display: "block", fontSize: 13, fontWeight: 400, color: "#374151" }}>
                  {competitorTitle}
                </span>
                <span style={{ display: "block", fontSize: 11.5, fontWeight: 300, color: "#9ca3af", marginTop: 3, letterSpacing: "0.04em" }}>
                  {competitorSubtitle}
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, i) => (
              <tr className="comparison-row" key={i} style={{ borderBottom: i < rows.length - 1 ? "1.5px solid #edf1f9" : "none" }}>
                <td
                  className="comparison-feature-cell"
                  style={{
                    padding: "15px 24px",
                    fontSize: 13,
                    fontWeight: 300,
                    color: "#1e293b",
                    lineHeight: 1.5,
                    background: "#fff",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                  }}
                >
                  {row.feature}
                </td>
                <td className="comparison-optra-cell" style={{ padding: "15px 0", textAlign: "center", background: "#eef5ff", borderLeft: "1.5px solid #dce8fb", boxShadow: "inset 0 0 0 1px rgba(111,168,250,0.22)" }}>
                  <Cell variant={row.optra} label={row.optraLabel} />
                </td>
                <td className="comparison-consultant-cell" style={{ padding: "15px 0", textAlign: "center", background: "#f9fafb", borderLeft: "1.5px solid #edf1f9" }}>
                  <Cell variant={row.consultant} label={row.consultantLabel} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div style={{ display: "flex", gap: 22, marginTop: 14, flexWrap: "wrap" }}>
        {LEGEND.map(({ color, border, label }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 300, color: "#94a3b8" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, border: border || "none", flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}
